import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { roundAtSecondDecimal } from '../numbers';
import { isNilOrEmpty } from '../objectUtils';
import { collapseWhitespace, isTwelveDigits } from '../strings';
import { BankAccount, BankAccountType, IbanStatus, RemittanceType } from '../types';

export const CLIENT_OWNED_BANK_ACCOUNT_TYPES: BankAccountType[] = [
  BankAccountType.AGENCY_ACCOUNT,
  BankAccountType.OWNER_PRIVATE_ACCOUNT,
  BankAccountType.THIRD_PARTY_BUILDING,
  BankAccountType.THIRD_PARTY_MULTI_OWNER,
  BankAccountType.THIRD_PARTY_OWNER,
];

export const isClientOwnedBankAccount = (bankAccount: Pick<BankAccount, 'type'>): boolean =>
  CLIENT_OWNED_BANK_ACCOUNT_TYPES.includes(bankAccount.type);

export interface OpenIbanResponse {
  valid: boolean;
  bankData: {
    name: string;
    bic: string;
  };
}

/* https://openiban.com/#additional-info
  {
  "valid": true, 		// either true or false
  "messages": [ 		// states the reason if "valid" is false
    "Bank code valid: 37040044"
  ],
  "iban": "DE89370400440532013000",	// the IBAN which was to be validated
  "bankData": {
    "bankCode": "37040044",
    "name": "Commerzbank",
    "zip": "50447",
    "city": "Köln",
    "bic": "COBADEFF3701"
  },
  "checkResults": {
    "bankCode": true 		// info about your additional check requests
  }
}
  */

export const getBicNumberAndBankName = async (ibanNumber: string): Promise<{ bicNumber: string; bankName: string }> => {
  try {
    const response = await fetch(`https://openiban.com/validate/${ibanNumber}?getBIC=true&validateBankCode=true`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });
    const data: OpenIbanResponse = (await response.json()) as OpenIbanResponse;
    if (data.valid) {
      return { bankName: data.bankData.name, bicNumber: data.bankData.bic };
    }
  } catch (err) {
    console.log(err);
  }
  return { bicNumber: '', bankName: '' };
};

/**
 * Calculates the control number based on the given numbers
 *
 * control = numbers % 97, unless numbers % 97 === 0, then
 * 97 is used as control.
 *
 * When the control is a single digit, a leading zero is added.
 *
 * @param  {String|Number} 	numbers number to calculate control for.
 * @return {String}         The control number.
 */
function calculateCheckDigits(numbers: string) {
  const MOD = 97;
  const number = parseInt(numbers, 10);
  let control = number % MOD;

  control = control === 0 ? 97 : control;

  return control < 10 ? '0' + control : control.toString();
}

/**
 * Checks whether the given OGM is valid. Returns true if valid,
 * false otherwise.
 *
 * @param  {String}  ogm The ogm to check
 * @return {Boolean}     truth value of validity.
 */
export function isValidOGM(structuredMessage: string) {
  return !isNil(findStructuredCommunication(structuredMessage));
}

const STRUCTURED_COMMUNICATION_PATTERN = /([+*])\1{2}\s?(\d{3})\s?\/\s?(\d{4})\s?\/\s?(\d{5})\s?\1{3}/g;

export const findStructuredCommunication = (remittanceInformation?: string | null): string | undefined => {
  if (isNil(remittanceInformation) || isEmpty(remittanceInformation)) {
    return undefined;
  }
  for (const [, , first, second, third] of remittanceInformation.matchAll(STRUCTURED_COMMUNICATION_PATTERN)) {
    const digits = `${first}${second}${third}`;
    if (calculateCheckDigits(digits.substring(0, 10)) === digits.substring(10, 12)) {
      return `+++${first}/${second}/${third}+++`;
    }
  }
  return undefined;
};

/**
 * Calculates the MOD 97 10 of the passed number as specified in ISO7064.
 *
 * @param number
 * @returns {number}
 */
export const getMod97 = (number: string) => {
  let remainder = number;
  let block;

  while (remainder.length > 2) {
    block = remainder.slice(0, 9);
    remainder = (parseInt(block, 10) % 97) + remainder.slice(block.length);
  }

  return parseInt(remainder, 10) % 97;
};

const BNP_PARIBAS_FORTIS_NO_COMMUNICATION = 'PAS DE COMMUNICATION';

const CODA_DUTCH_COMMUNICATION_PATTERN =
  /Mededeling:\s*([\s\S]*?)(?=\s+(Rekeningnummer:|Communicatie type:|Naam:|Verrichtingscode:|Refertenr|€|$))/i;

// The label of a field never repeats the case of another one, so matching them is case sensitive:
// "Communication:" is a field of its own, "Type communication:" is another one.
const CODA_FRENCH_FIELDS = "Compte bancaire:|Nom:|Code d'opération:|Type communication:|Nr référence|€";
const CODA_FRENCH_MESSAGE_PATTERN = new RegExp(`Message:\\s*([\\s\\S]*?)(?=\\s*(${CODA_FRENCH_FIELDS}|$))`);
const CODA_FRENCH_COMMUNICATION_PATTERN = new RegExp(`Communication:\\s*([\\s\\S]*?)(?=\\s*(${CODA_FRENCH_FIELDS}|$))`);

const CBC_TRANSFER_COMMUNICATION_PATTERN = /BIC:\s?[A-Z0-9]{8,11}\s*([\s\S]*?)(?=\s+Réf\.|\s+Tableau de bord|$)/;
const CBC_CARD_PAYMENT_MERCHANT_PATTERN = /^([\s\S]*?)\s+Paiement\s+[\s\S]*?par Carte de d[ée]bit/;
const CBC_CARD_STATEMENT_PATTERN = /^([\s\S]*?)(?:\s+(?:Numéro de carte|CARTE))?\s+\d{4} \d{2}XX XXXX \d{4}/;
const CBC_DIRECT_DEBIT_COMMUNICATION_PATTERN = /Domiciliation\s+\S+\s+\S+\s+([\s\S]+)$/;
const CBC_INVESTMENT_CREDIT_PATTERN = /^([\s\S]*?)\s+Capital\s/;

const BNP_PARIBAS_FORTIS_COMMUNICATION_PATTERN =
  /REFERENCE DONNEUR D'ORDRE :\s*\S+\s+([\s\S]*?)(?=\s*REFERENCE BANQUE)/;
const BNP_PARIBAS_FORTIS_OPERATION_PATTERN = /^([\s\S]*?)(?=\s*REFERENCE BANQUE)/;
const BNP_PARIBAS_FORTIS_COMMUNICATION_LABEL_PATTERN = /^COMMUNICATION\s*:\s*/;

const ING_INVOICE_PATTERN = /FACTURE n°\s*\S+\s+du\s+\d{2}\/\d{2}\/\d{4}/i;

const BELFIUS_COMMUNICATION_PATTERN = /REF\. : ([A-Z0-9]+) VAL\. ([0-9]{2})-([0-9]{2}) ([\s\S]*)/;

const STRUCTURED_COMMUNICATION_BLOCK_PATTERN = /\+{3}[\s\S]*?\+{3}/;

// Banks reformat references freely ("+++090/9337/55493+++" ↔ "090933755493" ↔ "090 / 9337 / 55493").
export const normalizeRemittanceReference = (reference?: string | null): string =>
  (reference ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

export const extractStructuredRemittanceBlock = (remittanceInformation?: string | null): string | undefined =>
  remittanceInformation?.match(STRUCTURED_COMMUNICATION_BLOCK_PATTERN)?.[0];

const extractDutchCodaRemittanceInformation = (remittanceInformation: string) => {
  const communication = remittanceInformation.match(CODA_DUTCH_COMMUNICATION_PATTERN);
  if (communication) {
    return collapseWhitespace(communication[1]);
  }
  return undefined;
};

const extractFrenchCodaRemittanceInformation = (remittanceInformation: string) => {
  const structuredMessage = remittanceInformation.match(CODA_FRENCH_MESSAGE_PATTERN);
  if (structuredMessage) {
    return collapseWhitespace(structuredMessage[1]);
  }
  const communication = remittanceInformation.match(CODA_FRENCH_COMMUNICATION_PATTERN);
  if (communication) {
    return collapseWhitespace(communication[1]);
  }
  return undefined;
};

const extractCbcRemittanceInformation = (remittanceInformation: string) => {
  const transferCommunication = remittanceInformation.match(CBC_TRANSFER_COMMUNICATION_PATTERN);
  if (transferCommunication) {
    return collapseWhitespace(transferCommunication[1]);
  }
  const cardPaymentMerchant = remittanceInformation.match(CBC_CARD_PAYMENT_MERCHANT_PATTERN);
  if (cardPaymentMerchant) {
    return collapseWhitespace(cardPaymentMerchant[1]);
  }
  const cardStatement = remittanceInformation.match(CBC_CARD_STATEMENT_PATTERN);
  if (cardStatement) {
    return collapseWhitespace(cardStatement[1]);
  }
  const directDebitCommunication = remittanceInformation.match(CBC_DIRECT_DEBIT_COMMUNICATION_PATTERN);
  if (directDebitCommunication) {
    return collapseWhitespace(directDebitCommunication[1]);
  }
  const investmentCredit = remittanceInformation.match(CBC_INVESTMENT_CREDIT_PATTERN);
  if (investmentCredit) {
    return collapseWhitespace(investmentCredit[1]);
  }
  return undefined;
};

const extractBnpParibasFortisRemittanceInformation = (remittanceInformation: string) => {
  const communication = remittanceInformation.match(BNP_PARIBAS_FORTIS_COMMUNICATION_PATTERN);
  if (communication) {
    const collapsedCommunication = collapseWhitespace(communication[1]).replace(
      BNP_PARIBAS_FORTIS_COMMUNICATION_LABEL_PATTERN,
      ''
    );
    return collapsedCommunication === BNP_PARIBAS_FORTIS_NO_COMMUNICATION ? '' : collapsedCommunication;
  }
  const operation = remittanceInformation.match(BNP_PARIBAS_FORTIS_OPERATION_PATTERN);
  if (operation) {
    return collapseWhitespace(operation[1]);
  }
  return undefined;
};

const extractIngRemittanceInformation = (remittanceInformation: string) => {
  const invoice = remittanceInformation.match(ING_INVOICE_PATTERN);
  if (invoice) {
    return collapseWhitespace(invoice[0]);
  }
  return undefined;
};

const extractBelfiusRemittanceInformation = (remittanceInformation: string) => {
  const communication = remittanceInformation.match(BELFIUS_COMMUNICATION_PATTERN);
  if (communication) {
    return communication[4].trim();
  }
  return undefined;
};

const REMITTANCE_INFORMATION_EXTRACTORS = [
  extractDutchCodaRemittanceInformation,
  extractFrenchCodaRemittanceInformation,
  extractBelfiusRemittanceInformation,
  extractCbcRemittanceInformation,
  extractBnpParibasFortisRemittanceInformation,
  extractIngRemittanceInformation,
];

const keepStructuredCommunicationBlock = (remittanceInformation: string) => {
  const structuredCommunicationBlock = remittanceInformation.match(STRUCTURED_COMMUNICATION_BLOCK_PATTERN);
  if (structuredCommunicationBlock) {
    return structuredCommunicationBlock[0];
  }
  return remittanceInformation;
};

/**
 * Get printable string based on remittanceInformation and remittanceInformationType.
 * If it's a structured remittance information, it will display it with space.
 *
 * @param remittanceInformation The remittance information we want to display
 * @param remittanceInformationType Can be null or structured or unstructured
 * @returns {string} displayable string (can be empty)
 */
export const getPrintableRemittanceInformation = (
  remittanceInformation?: string | null,
  remittanceInformationType?: string | null
) => {
  if (isNil(remittanceInformation) || isEmpty(remittanceInformation)) {
    return '';
  }
  const structuredCommunication = findStructuredCommunication(remittanceInformation);
  if (!isNil(structuredCommunication)) {
    return printStructuredRemittanceInformation(structuredCommunication);
  }

  if (remittanceInformationType === RemittanceType.structured) {
    return printStructuredRemittanceInformation(remittanceInformation.trim());
  }

  for (const extractRemittanceInformation of REMITTANCE_INFORMATION_EXTRACTORS) {
    const extractedRemittanceInformation = extractRemittanceInformation(remittanceInformation);
    if (!isNil(extractedRemittanceInformation)) {
      return printStructuredRemittanceInformation(keepStructuredCommunicationBlock(extractedRemittanceInformation));
    }
  }

  return keepStructuredCommunicationBlock(remittanceInformation.trim());
};

/**
 * Check if the remittanceInformation is structured or unstructured.
 *
 * @param remittanceInformation
 * @returns {string} structured or unstructured
 */
export const getRemittanceInformationType = (remittanceInformation?: string | null) => {
  if (isNil(remittanceInformation) || isEmpty(remittanceInformation)) {
    return RemittanceType.unstructured;
  }
  if (!isNil(findStructuredCommunication(remittanceInformation))) {
    return RemittanceType.structured;
  }
  return RemittanceType.unstructured;
};

/**
 * Print the structured remittanceInformation in this format: +++ 000 / 0000 / 00000 +++
 *
 * @param remittanceInformation
 * @returns {string} formated remittance information
 */
export const printStructuredRemittanceInformation = (remittanceInformation?: string | null, separator = ' ') => {
  if (isNil(remittanceInformation) || isEmpty(remittanceInformation)) {
    return '';
  }

  if (getRemittanceInformationType(remittanceInformation) !== RemittanceType.structured) {
    if (isTwelveDigits(remittanceInformation)) {
      return remittanceInformation.replace(
        /^(\d{3})(\d{4})(\d{5})$/,
        `+++${separator}$1${separator}/${separator}$2${separator}/${separator}$3${separator}+++`
      );
    }
    return remittanceInformation;
  }
  const remit = remittanceInformation;
  return remit.replace(
    /^([+][+][+])\s?(\d\d\d)\s?([/])\s?(\d\d\d\d)\s?([/])\s?(\d\d\d\d\d)\s?([+][+][+])$/g,
    '$1' +
      separator +
      '$2' +
      separator +
      '$3' +
      separator +
      '$4' +
      separator +
      '$5' +
      separator +
      '$6' +
      separator +
      '$7'
  );
};

/**
 * Encode the give number in the structured remittance information format
 * @param {string|undefined|null} remittanceInformation
 */
export const encodeStructuredRemittanceInformation = (remittanceInformation?: string | null) => {
  if (isNil(remittanceInformation) || isEmpty(remittanceInformation)) {
    return '';
  }
  if (remittanceInformation.length !== 12) {
    return remittanceInformation;
  }
  return (
    '+++' +
    remittanceInformation.substring(0, 3) +
    '/' +
    remittanceInformation.substring(3, 7) +
    '/' +
    remittanceInformation.substring(7, 12) +
    '+++'
  );
};

/**
 * Add 0 to the left of the number to get the desired length
 * @param {*} number The number to fill with 0 in the left
 * @param {*} length The length of the string
 */
export const leftPadWithZeros = (number: number, length: number) => {
  const str = `${number}`;
  const allZeros = Array(length - str.length)
    .fill('0')
    .join('');
  return `${allZeros}${str}`;
};

/**
 * Generate a structured remittance information randomly
 */
export const generateStructuredRemittanceInformation = () => {
  const result = leftPadWithZeros(Math.floor(Math.random() * 10000000000), 10);
  const finalValue = encodeStructuredRemittanceInformation(result + calculateCheckDigits(result));
  return finalValue;
};

export const STRUCTURED_REMITTANCE_DIGITS = 12;

const STRUCTURED_REMITTANCE_MISSING_DIGIT = '_';
const STRUCTURED_REMITTANCE_FIRST_GROUP_END = 3;
const STRUCTURED_REMITTANCE_SECOND_GROUP_END = 7;

export const toStructuredRemittanceDigits = (value: string | null | undefined): string =>
  (value ?? '').replace(/\D/g, '').slice(0, STRUCTURED_REMITTANCE_DIGITS);

export const formatStructuredRemittanceDraft = (digits: string): string => {
  const draftDigits = toStructuredRemittanceDigits(digits);
  if (isEmpty(draftDigits)) {
    return '';
  }
  const maskedDigits = draftDigits.padEnd(STRUCTURED_REMITTANCE_DIGITS, STRUCTURED_REMITTANCE_MISSING_DIGIT);
  const firstGroup = maskedDigits.slice(0, STRUCTURED_REMITTANCE_FIRST_GROUP_END);
  const secondGroup = maskedDigits.slice(STRUCTURED_REMITTANCE_FIRST_GROUP_END, STRUCTURED_REMITTANCE_SECOND_GROUP_END);
  const thirdGroup = maskedDigits.slice(STRUCTURED_REMITTANCE_SECOND_GROUP_END);
  return `+++ ${firstGroup} / ${secondGroup} / ${thirdGroup} +++`;
};

export const encodeStructuredRemittanceDraft = (digits: string): string =>
  digits.length === STRUCTURED_REMITTANCE_DIGITS ? encodeStructuredRemittanceInformation(digits) : digits;

export const getCleanBankAccountNumber = (bankAccountNumber: string) =>
  bankAccountNumber.toUpperCase().replace(/\s+/g, '');

export const isBankAccountIbanSafe = (
  bankAccount: Pick<BankAccount, 'ibanStatus' | 'ibanVerifiedNumber' | 'number'>
): boolean =>
  bankAccount.ibanStatus === IbanStatus.SAFE &&
  !isNil(bankAccount.ibanVerifiedNumber) &&
  getCleanBankAccountNumber(bankAccount.ibanVerifiedNumber) === getCleanBankAccountNumber(bankAccount.number);

const IBAN_STATUS_RANK: Record<IbanStatus, number> = {
  [IbanStatus.SAFE]: 0,
  [IbanStatus.OBSERVED]: 1,
  [IbanStatus.KNOWN]: 2,
};

export const getEffectiveIbanStatus = (
  bankAccount: Pick<BankAccount, 'ibanStatus' | 'ibanVerifiedNumber' | 'number'>
): IbanStatus => {
  if (isBankAccountIbanSafe(bankAccount)) {
    return IbanStatus.SAFE;
  }
  if (isNil(bankAccount.ibanStatus) || bankAccount.ibanStatus === IbanStatus.SAFE) {
    return IbanStatus.KNOWN;
  }
  return bankAccount.ibanStatus;
};

export const compareByEffectiveIbanStatus = (
  left: Pick<BankAccount, 'ibanStatus' | 'ibanVerifiedNumber' | 'number'>,
  right: Pick<BankAccount, 'ibanStatus' | 'ibanVerifiedNumber' | 'number'>
): number => IBAN_STATUS_RANK[getEffectiveIbanStatus(left)] - IBAN_STATUS_RANK[getEffectiveIbanStatus(right)];

export const findStructuredOrBareCommunication = (text?: string | null): string | undefined => {
  const printedCommunication = findStructuredCommunication(text);
  if (!isNil(printedCommunication)) {
    return printedCommunication;
  }
  const value = text?.trim() ?? '';
  return isTwelveDigits(value) ? findStructuredCommunication(encodeStructuredRemittanceInformation(value)) : undefined;
};

export interface LeaseBankAccountAvailabilityContext {
  ownerIds: string[];
  buildingIds: string[];
}

export const isThirdPartyAccountAvailableForLease = (
  bankAccount: Pick<BankAccount, 'type' | 'contactIds' | 'buildingIds'>,
  { ownerIds, buildingIds }: LeaseBankAccountAvailabilityContext
): boolean => {
  if (
    bankAccount.type === BankAccountType.THIRD_PARTY_OWNER ||
    bankAccount.type === BankAccountType.THIRD_PARTY_MULTI_OWNER
  ) {
    return (bankAccount.contactIds ?? []).some((contactId) => ownerIds.includes(contactId));
  }
  if (bankAccount.type === BankAccountType.THIRD_PARTY_BUILDING) {
    return (bankAccount.buildingIds ?? []).some((buildingId) => buildingIds.includes(buildingId));
  }
  return false;
};

export const THIRD_PARTY_BANK_ACCOUNT_TYPES = [
  BankAccountType.THIRD_PARTY_OWNER,
  BankAccountType.THIRD_PARTY_MULTI_OWNER,
  BankAccountType.THIRD_PARTY_BUILDING,
];

export const getBankAccountOwnerIds = ({
  type,
  ownerId,
  contactIds,
}: {
  type: BankAccountType;
  ownerId?: string | null;
  contactIds?: string[] | null;
}): string[] => {
  if (type === BankAccountType.THIRD_PARTY_MULTI_OWNER) {
    return contactIds ?? [];
  }
  if (type === BankAccountType.THIRD_PARTY_OWNER && !isNilOrEmpty(ownerId)) {
    return [ownerId];
  }
  return [];
};

export type StoredBankAccountOwnerFields = Pick<BankAccount, 'contactId' | 'type'> & {
  contactIds?: Array<string | null> | null;
};

export const getStoredBankAccountOwnerIds = ({
  contactId,
  contactIds,
  type,
}: StoredBankAccountOwnerFields): string[] => {
  const linkedOwnerIds = compact(contactIds ?? []);
  if (type === BankAccountType.THIRD_PARTY_MULTI_OWNER) {
    return linkedOwnerIds;
  }
  if (type === BankAccountType.THIRD_PARTY_OWNER) {
    return isEmpty(linkedOwnerIds) ? [contactId] : linkedOwnerIds;
  }
  return [];
};

export interface BankAccountsBalance {
  total: number | null;
  partial: boolean;
}

export const getBankAccountsBalance = (bankAccounts: BankAccount[]): BankAccountsBalance => {
  const bankAccountsWithBalance = bankAccounts.filter((bankAccount) => !isNil(bankAccount.balance));
  if (isEmpty(bankAccountsWithBalance)) {
    return { total: null, partial: false };
  }
  const total = bankAccountsWithBalance.reduce(
    (accumulator, bankAccount) => accumulator + (bankAccount.balance ?? 0),
    0
  );
  return {
    total: roundAtSecondDecimal(total),
    partial: bankAccountsWithBalance.length !== bankAccounts.length,
  };
};
